import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { ChartVisualization } from '../ChartVisualization/ChartVisualization'
import { ClusterVisualization } from '../ClusterVisualization/ClusterVisualization'
import { ProcessingSteps } from '../ProcessingSteps/ProcessingSteps'
import { ScoopText, ScoopDivider } from '../../../../ui'
import {
    QueryDetails,
    FindingsSection,
    RecommendationsSection,
    FollowUpPrompts,
    DataLimitationsSection,
    FeedbackSection,
    StructuredMessageTable,
} from './StructuredMessageComponents'
import styles from './StructuredMessage.module.scss'

export const StructuredMessage = ({
    message,
    onSendFollowUp,
    showProcessingSteps = true,
    showQueryDetails = true,
    isPublic,
    isProcessing,
    isEmbedded = false,
    handleExport,
    isLastVisualization = false,
    selectedInbox,
    setSelectedTab,
    mlConfig,
    setMlConfig,
    setMLResults,
    submitFeedback,
    isSubmittingFeedback,
    isLastMessage,
    isCurrentSession,
    isTourWorkspace,
}) => {
    // Check for content in the actual structure
    const contentText = message.content?.text

    // Visualization data location differs between quick and deep analysis:
    // Quick: chart config in content.visualization
    // Deep: chart config in content.text.visualization
    const chartVisualization =
        contentText?.view && contentText?.selectedItems ? contentText : contentText?.visualization

    // Check for cluster data
    const clusterData = contentText?.resultType === 'clusterResult' ? contentText : null

    const hasContent =
        contentText?.response ||
        chartVisualization ||
        clusterData ||
        contentText?.dataTable ||
        contentText?.dataset ||
        contentText?.columns ||
        contentText?.bullets?.length > 0 ||
        contentText?.findings?.length > 0 ||
        contentText?.recommendations?.length > 0 ||
        contentText?.dataLimitations?.length > 0

    const hasFooter =
        message.footer?.summary ||
        contentText?.followOnPrompts?.length > 0 ||
        contentText?.vizTable?.followOnPrompts?.length > 0 ||
        message.content?.suggestions?.length > 0 ||
        contentText?.suggestions?.length > 0

    // Extract correlationID from message data (located in header.correlationID)
    const correlationID = message.rawData?.header?.correlationID

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                {/* Processing steps if available */}
                {message.processingSteps?.length > 0 && showProcessingSteps && (
                    <div className={styles.processingStepsBox}>
                        <ProcessingSteps
                            steps={message.processingSteps}
                            isComplete={true}
                            showSteps={showProcessingSteps}
                        />
                    </div>
                )}

                {/* HEADER SECTION - Check for execution metadata */}
                {(contentText?.execution_time_ms ||
                    contentText?.probe_count ||
                    contentText?.confidence_level) && (
                    <div
                        className={`${styles.headerBox} ${hasContent ? styles.hasContent : styles.noContent}`}
                    >
                        <div className={styles.headerMetadata}>
                            {contentText.confidence_level && (
                                <ScoopText className={styles.metadataItem}>
                                    {contentText.confidence_level === 'high'
                                        ? '🟢'
                                        : contentText.confidence_level === 'medium'
                                          ? '🟡'
                                          : contentText.confidence_level === 'low'
                                            ? '🔴'
                                            : '⚪'}{' '}
                                    Confidence: {contentText.confidence_level.toUpperCase()}
                                </ScoopText>
                            )}
                            {contentText.probe_count && (
                                <ScoopText className={styles.metadataItem}>
                                    🔍 {contentText.probe_count} queries
                                </ScoopText>
                            )}
                        </div>
                    </div>
                )}

                {/* Divider between header and content */}
                {(contentText?.execution_time_ms || contentText?.probe_count) && hasContent && (
                    <ScoopDivider className={styles.divider} />
                )}

                {/* CONTENT SECTION */}
                {hasContent && (
                    <div
                        className={`${styles.contentBox} ${hasFooter ? styles.hasFooter : styles.noFooter}`}
                    >
                        {/* Text content (from content.text.response) */}
                        {contentText?.response && (
                            <div className={styles.responseBox}>
                                <ScoopText className={styles.responseText}>
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {contentText.response}
                                    </ReactMarkdown>
                                </ScoopText>
                            </div>
                        )}

                        {/* Visualization - Render actual chart */}
                        {/* Quick: Chart.js config in content.visualization, Explorer config in content.text */}
                        {/* Deep: Both configs in content.text.visualization */}
                        {chartVisualization && (
                            <div className={styles.visualizationBox}>
                                <ChartVisualization
                                    visualization={chartVisualization}
                                    isPublic={isPublic}
                                    message={message}
                                    isEmbedded={isEmbedded}
                                    handleExport={handleExport}
                                    isLastVisualization={isLastVisualization}
                                    isTourWorkspace={isTourWorkspace}
                                />
                            </div>
                        )}

                        {/* Cluster Visualization - Render cluster analysis results */}
                        {clusterData && (
                            <div className={styles.clusterBox}>
                                <ClusterVisualization
                                    clusterData={clusterData}
                                    isPublic={isPublic}
                                    isEmbedded={isEmbedded}
                                    selectedInbox={selectedInbox}
                                    setSelectedTab={setSelectedTab}
                                    mlConfig={mlConfig}
                                    setMlConfig={setMlConfig}
                                    setMLResults={setMLResults}
                                    isTourWorkspace={isTourWorkspace}
                                />
                            </div>
                        )}

                        {/* Data Table - Render table data */}
                        {/* Handle different table data formats */}
                        {/* Don't render table for cluster results */}
                        {!clusterData &&
                            (contentText?.dataTable ||
                                contentText?.dataset ||
                                (contentText?.columns && contentText?.rows)) && (
                                <div className={styles.dataTableBox}>
                                    {/* Show AI Summary if available */}
                                    {contentText?.vizTable?.aiSummary && (
                                        <ScoopText className={styles.aiSummary}>
                                            {contentText.vizTable.aiSummary}
                                        </ScoopText>
                                    )}

                                    {/* Show table title if available */}
                                    {contentText?.vizTable?.title && (
                                        <ScoopText className={styles.tableTitle}>
                                            {contentText.vizTable.title}
                                        </ScoopText>
                                    )}

                                    <div className={styles.tableContainer}>
                                        <StructuredMessageTable
                                            data={(() => {
                                                // Handle dataTable format (from API spec)
                                                if (contentText.dataTable) {
                                                    return {
                                                        columns:
                                                            contentText.dataTable.headers?.map(
                                                                (header, idx) => ({
                                                                    columnName: header,
                                                                    columnType:
                                                                        typeof contentText.dataTable
                                                                            .rows?.[0]?.[idx] ===
                                                                        'number'
                                                                            ? 'Decimal'
                                                                            : 'String',
                                                                })
                                                            ) || [],
                                                        rows: contentText.dataTable.rows || [],
                                                    }
                                                }
                                                // Handle dataset format
                                                if (contentText.dataset) {
                                                    return contentText.dataset
                                                }
                                                // Handle direct columns/rows format (actual backend format)
                                                if (contentText.columns) {
                                                    return {
                                                        columns: contentText.columns,
                                                        rows: contentText.rows || [],
                                                    }
                                                }
                                                return { columns: [], rows: [] }
                                            })()}
                                        />
                                    </div>
                                </div>
                            )}

                        {/* Show query details from content.visualization if available */}
                        {showQueryDetails &&
                            (message.content?.visualization?.query ||
                                message.content?.visualization?.queryType ||
                                message.content?.visualization?.datasetName ||
                                message.content?.visualization?.metrics ||
                                message.content?.visualization?.attributes ||
                                contentText?.vizTable ||
                                contentText?.visualizationRecommendation) && (
                                <QueryDetails message={message} contentText={contentText} />
                            )}

                        {/* Bullets */}
                        {contentText?.bullets?.length > 0 && (
                            <div className={styles.bulletsBox}>
                                {contentText.bullets.map((bullet, i) => (
                                    <div key={i} className={styles.bulletItem}>
                                        <ScoopText className={styles.bulletIcon}>•</ScoopText>
                                        <ScoopText className={styles.bulletText}>
                                            {bullet.summary}
                                        </ScoopText>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Findings */}
                        <FindingsSection findings={contentText?.findings} />

                        {/* Recommendations */}
                        <RecommendationsSection recommendations={contentText?.recommendations} />

                        {/* Data Limitations */}
                        <DataLimitationsSection dataLimitations={contentText?.dataLimitations} />
                    </div>
                )}

                {/* Divider between content and footer */}
                {hasContent && hasFooter && <ScoopDivider className={styles.divider} />}

                {/* FOOTER SECTION */}
                {hasFooter && (
                    <div className={styles.footerBox}>
                        {/* Summary */}
                        {message.footer?.summary && (
                            <ScoopText className={styles.footerSummary}>
                                {message.footer.summary}
                            </ScoopText>
                        )}

                        {/* Follow-up prompts - can be in multiple places */}
                        <FollowUpPrompts
                            prompts={
                                contentText?.followOnPrompts ||
                                contentText?.vizTable?.followOnPrompts ||
                                message.content?.suggestions ||
                                contentText?.suggestions
                            }
                            onSendFollowUp={onSendFollowUp}
                            isProcessing={isProcessing}
                        />

                        {/* Metadata */}
                        {message.metadata?.rowCount !== undefined && (
                            <ScoopText className={styles.metadata}>
                                {message.metadata.rowCount !== undefined &&
                                    `${message.metadata.rowCount} rows returned`}
                            </ScoopText>
                        )}
                    </div>
                )}

                {/* FEEDBACK SECTION - Show for result responses only */}
                {hasContent && !isEmbedded && isLastMessage && isCurrentSession && (
                    <FeedbackSection
                        correlationID={correlationID}
                        messageID={message.messageID}
                        hasFooter={hasFooter}
                        hasContent={hasContent}
                        submitFeedback={submitFeedback}
                        isSubmittingFeedback={isSubmittingFeedback}
                    />
                )}
            </div>
        </div>
    )
}
